// app/(tabs)/market/boutiques/[slug].tsx
//
// ÉCRAN PUBLIC — reconstruit à partir de zéro pour fonctionner SANS
// session active (visiteurs non connectés inclus). Tout vit dans ce
// seul fichier : fetch, header de boutique, thème dynamique (ui_config)
// et grille de produits — plus de ShopHeader.tsx ni de ThemeProvider.tsx
// séparés, par choix explicite (même principe que produits/[id].tsx).
//
// SOURCE DE DONNÉES : POST vers /url-public-market (backend, voir
// handleUrlPublicMarket.ts), action 'get-shop-public', avec
// { slug }. AUCUN access_token envoyé. Réponse : { shop, products }
// (products = TOUS les produits publiés de la boutique, pas de
// pagination côté serveur pour cet écran public).
//
// THÈME (ui_config) : shop.ui_config.colors est appliqué directement en
// styles inline sur les éléments concernés (nom, prix, boutons,
// badges) — reprend exactement les clés déjà utilisées par
// useMarketTheme() dans l'ancienne version (primary, card, border,
// text, secondary, background), sans Context/Provider : juste des
// variables lues une fois shop chargé, réutilisées dans le JSX.
// shop.ui_config.header contrôle l'affichage conditionnel de
// tagline/description/adresse/téléphone/email/WhatsApp business dans
// le bloc d'en-tête (show_tagline, show_description, show_address,
// show_phone, show_email, show_whatsapp_business).
//
// ABONNEMENT : pas de session ⇒ pas de useSubscriptions ici. Le bouton
// reste affiché (isSubscribed toujours false visuellement) mais son
// clic redirige vers la page de connexion du site
// (https://www.harmoniaworld.world/login) via Linking.openURL — décision
// explicite validée pour cet écran.
//
// AUTOSCROLL + DOUBLE TAP : même système que partout ailleurs dans
// Market. Pas de header interne animé par translateY ici — seule la tab
// bar globale est cachée/montrée. La pagination n'existe plus au sens
// scroll infini (tous les produits sont chargés en un seul appel), donc
// onScrollExtra n'a plus besoin de logique de pagination — conservé
// vide/optionnel, seul l'auto-hide au scroll reste utile.
//
// PATCH i18n — Passage du texte affiché par i18next (useTranslation /
// t()). Ajout d'un sélecteur de langue flottant en haut à droite (même
// système que landing.tsx : i18n.changeLanguage + saveLanguage), car cet
// écran public n'a pas de navbar existante pour l'y intégrer. Icônes,
// couleurs, IDs, endpoints, structure de données et ui_config restent
// codés en dur, inchangés.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  Platform,
  Image,
  Linking,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { useTranslation } from 'react-i18next';

import { useScrollAutoHide } from '@/hooks/useScrollAutoHide';
import { useTabBarVisibility } from '@/components/CustomTabBar';
import { saveLanguage } from '@/services/storage';

const NATIVE = Platform.OS !== 'web';
const API_BASE_URL = 'https://eueke282zksk1zki18susjdksisk18sj.onrender.com';
const LOGIN_URL = 'https://www.harmoniaworld.world/login';

// ── Langues disponibles pour le sélecteur (même liste que landing.tsx) ──
const LANGUAGES = [
  { code: 'fr', flag: '🇫🇷', label: 'FR' },
  { code: 'en', flag: '🇬🇧', label: 'EN' },
  { code: 'es', flag: '🇪🇸', label: 'ES' },
  { code: 'pt', flag: '🇵🇹', label: 'PT' },
];

// ── Formes de données telles que renvoyées par get-shop-public ──
// (voir handleUrlPublicMarket.ts)

type UiConfigColors = {
  card: string;
  link: string;
  text: string;
  accent: string;
  border: string;
  button: string;
  primary: string;
  secondary: string;
  background: string;
};

type UiConfigHeader = {
  show_email: boolean;
  show_phone: boolean;
  show_address: boolean;
  show_tagline: boolean;
  show_description: boolean;
  show_whatsapp_business: boolean;
};

type UiConfig = {
  colors: UiConfigColors;
  header: UiConfigHeader;
  layout: {
    shadows: boolean;
    max_width: string;
    card_spacing: string;
    display_mode: string;
    grid_columns: number;
    border_radius: string;
  };
  products: {
    card_size: string;
    show_price: boolean;
    show_stock: boolean;
    show_badges: boolean;
    show_action_buttons: boolean;
  };
  theme_mode: string;
  typography: {
    text_size: string;
    font_family: string;
    heading_size: string;
    heading_weight: string;
  };
};

type PublicShop = {
  id: string;
  name: string;
  slug: string;
  tagline: string | null;
  description: string | null;
  cover_image_url: string | null;
  whatsapp_business_phone: string | null;
  show_whatsapp_business_button: boolean;
  show_platform_chat_button: boolean;
  contact_email: string | null;
  contact_phone: string | null;
  country: string | null;
  city: string | null;
  address: string | null;
  currency_code: string;
  language_code: string;
  is_verified: boolean;
  is_premium: boolean;
  seo_title: string | null;
  seo_description: string | null;
  og_image_url: string | null;
  ui_config: UiConfig;
  published_at: string | null;
};

type PublicProduct = {
  id: string;
  category_id: string;
  name: string;
  slug: string;
  description_short: string | null;
  description: string | null;
  brand: string | null;
  condition: 'new' | 'used' | 'refurbished';
  cover_image_url: string | null;
  image_2_url: string | null;
  image_3_url: string | null;
  price: number;
  promo_price: number | null;
  currency_code: string;
  stock_quantity: number;
  is_featured: boolean;
  show_whatsapp_business_button: boolean;
  whatsapp_business_phone_override: string | null;
  show_platform_chat_button: boolean;
  show_buy_button: boolean;
  buy_url: string | null;
  show_learn_more_button: boolean;
  learn_more_url: string | null;
  show_call_button: boolean;
  call_phone: string | null;
  seo_title: string | null;
  seo_description: string | null;
  og_image_url: string | null;
  published_at: string | null;
};

function formatPrice(amount: number, currency: string) {
  return `${amount.toLocaleString('fr-FR')} ${currency}`;
}

// ── Sélecteur de langue flottant (même système que landing.tsx) ──
function FloatingLanguagePicker() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const currentLanguage = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  const handleChangeLanguage = useCallback(async (code: string) => {
    await i18n.changeLanguage(code);
    await saveLanguage(code);
    setOpen(false);
  }, [i18n]);

  return (
    <>
      <TouchableOpacity
        onPress={() => setOpen((v) => !v)}
        style={langStyles.trigger}
        activeOpacity={0.8}
      >
        <Text style={langStyles.flag}>{currentLanguage.flag}</Text>
        <Text style={langStyles.label}>{currentLanguage.label}</Text>
        <Ionicons name="chevron-down" size={12} color="#4B0082" />
      </TouchableOpacity>

      {open && (
        <TouchableOpacity
          style={langStyles.backdrop}
          activeOpacity={1}
          onPress={() => setOpen(false)}
        >
          <View style={langStyles.menu}>
            {LANGUAGES.map((lang) => (
              <TouchableOpacity
                key={lang.code}
                onPress={() => handleChangeLanguage(lang.code)}
                style={[
                  langStyles.menuItem,
                  lang.code === currentLanguage.code && langStyles.menuItemActive,
                ]}
              >
                <Text style={langStyles.menuFlag}>{lang.flag}</Text>
                <Text style={langStyles.menuLabel}>{lang.label}</Text>
                {lang.code === currentLanguage.code && (
                  <Ionicons name="checkmark" size={16} color="#6610B1" />
                )}
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      )}
    </>
  );
}

// Carte produit compacte, stylée avec la couleur primaire de la
// boutique — reçoit les couleurs en prop plutôt que via un Context
// (pas de ThemeProvider ici, cf. en-tête).
function ThemedProductCard({
  product,
  colors,
  onPress,
}: {
  product: PublicProduct;
  colors: UiConfigColors;
  onPress: () => void;
}) {
  const { t } = useTranslation();
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = !!product.cover_image_url && !imageFailed;

  return (
    <TouchableOpacity style={styles.productCard} activeOpacity={0.7} onPress={onPress}>
      {showImage ? (
        <View style={styles.productImageWrap}>
          <Image
            source={{ uri: product.cover_image_url! }}
            style={styles.productImage}
            resizeMode="cover"
            onError={() => setImageFailed(true)}
          />
          {product.is_featured && (
            <View style={[styles.featuredBadge, { backgroundColor: colors.primary }]}>
              <Text style={styles.featuredBadgeText}>{t('publicShopScreen.newBadge')}</Text>
            </View>
          )}
        </View>
      ) : (
        <View style={styles.productImagePlaceholder}>
          <Ionicons name="image-outline" size={24} color="#C4B5FD" />
          {product.is_featured && (
            <View style={[styles.featuredBadge, { backgroundColor: colors.primary }]}>
              <Text style={styles.featuredBadgeText}>{t('publicShopScreen.newBadge')}</Text>
            </View>
          )}
        </View>
      )}
      <Text style={styles.productName} numberOfLines={1}>{product.name}</Text>
      <View style={styles.priceRow}>
        {product.promo_price ? (
          <>
            <Text style={[styles.promoPrice, { color: colors.primary }]}>
              {formatPrice(product.promo_price, product.currency_code)}
            </Text>
            <Text style={styles.originalPrice}>{formatPrice(product.price, product.currency_code)}</Text>
          </>
        ) : (
          <Text style={[styles.price, { color: colors.primary }]}>
            {formatPrice(product.price, product.currency_code)}
          </Text>
        )}
      </View>
    </TouchableOpacity>
  );
}

export default function PublicShopDetailScreen() {
  const { t } = useTranslation();
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const router = useRouter();

  // ── Fetch direct, inline, sans session ──
  const [shop, setShop] = useState<PublicShop | null>(null);
  const [products, setProducts] = useState<PublicProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const fetchShop = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const response = await fetch(`${API_BASE_URL}/url-public-market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get-shop-public', slug }),
      });

      if (response.status === 404) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const json = await response.json();

      if (!response.ok || !json.success) {
        setError(json.error || t('publicShopScreen.errors.cannotLoadShop'));
        setLoading(false);
        return;
      }

      setShop(json.shop);
      setProducts(json.products || []);
    } catch (err) {
      setError(t('publicShopScreen.errors.cannotReachServer'));
    } finally {
      setLoading(false);
    }
  }, [slug, t]);

  useEffect(() => {
    fetchShop();
  }, [fetchShop]);

  // ── AutoScroll + double tap (tab bar uniquement) ──
  const { setTabBarHidden } = useTabBarVisibility();
  const tabBarHiddenRef = useRef(false);
  const [lastTap, setLastTap] = useState<number | null>(null);

  const showTabBar = useCallback(() => {
    if (tabBarHiddenRef.current) {
      tabBarHiddenRef.current = false;
      setTabBarHidden(false);
    }
  }, [setTabBarHidden]);

  const hideTabBar = useCallback(() => {
    if (!tabBarHiddenRef.current) {
      tabBarHiddenRef.current = true;
      setTabBarHidden(true);
    }
  }, [setTabBarHidden]);

  const { handleScroll } = useScrollAutoHide({
    onShow: showTabBar,
    onHide: hideTabBar,
  });

  const toggleTabBar = useCallback(() => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    if (tabBarHiddenRef.current) showTabBar();
    else hideTabBar();
  }, [showTabBar, hideTabBar]);

  const handleDoubleTap = useCallback(() => {
    const now = Date.now();
    if (lastTap && now - lastTap < 300) toggleTabBar();
    else setLastTap(now);
  }, [lastTap, toggleTabBar]);

  // ── Actions ──
  const handleProductPress = useCallback((product: PublicProduct) => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    router.push(`/produits/${product.id}`);
  }, [router]);

  // Pas de session ⇒ pas de useSubscriptions. Le clic redirige vers la
  // page de connexion du site, décision explicite validée pour cet écran.
  const handleSubscribePress = useCallback(() => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    Linking.openURL(LOGIN_URL);
  }, []);

  const handleWhatsApp = useCallback(() => {
    if (!shop?.whatsapp_business_phone) return;
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    const cleanPhone = shop.whatsapp_business_phone.replace(/[^\d]/g, '');
    const message = encodeURIComponent(t('publicShopScreen.whatsappMessage', { name: shop.name }));
    Linking.openURL(`https://wa.me/${cleanPhone}?text=${message}`);
  }, [shop, t]);

  const handleCall = useCallback(() => {
    if (!shop?.contact_phone) return;
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    Linking.openURL(`tel:${shop.contact_phone}`);
  }, [shop]);

  const handleEmail = useCallback(() => {
    if (!shop?.contact_email) return;
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    Linking.openURL(`mailto:${shop.contact_email}`);
  }, [shop]);

  // ── États de chargement / erreur ──

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <FloatingLanguagePicker />
        <ActivityIndicator size="small" color="#6610B1" />
        <Text style={styles.centerText}>{t('publicShopScreen.loadingShop')}</Text>
      </View>
    );
  }

  if (notFound) {
    return (
      <View style={styles.centerContainer}>
        <FloatingLanguagePicker />
        <Ionicons name="alert-circle-outline" size={48} color="#C4B5FD" />
        <Text style={styles.centerText}>{t('publicShopScreen.errors.shopNotFound')}</Text>
      </View>
    );
  }

  if (error || !shop) {
    return (
      <View style={styles.centerContainer}>
        <FloatingLanguagePicker />
        <Ionicons name="cloud-offline-outline" size={48} color="#C4B5FD" />
        <Text style={styles.centerText}>{error || t('publicShopScreen.errors.cannotLoadShop')}</Text>
        <TouchableOpacity style={styles.backBtn} onPress={fetchShop} activeOpacity={0.8}>
          <Text style={styles.backBtnText}>{t('publicShopScreen.retry')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const colors = shop.ui_config.colors;
  const header = shop.ui_config.header;

  const metaTitle = shop.seo_title?.trim() || shop.name;
  const metaDescription = shop.seo_description?.trim() || shop.tagline?.trim() || undefined;
  const metaImage = shop.og_image_url?.trim() || shop.cover_image_url || undefined;

  return (
    <>
      <Head>
        <title>{metaTitle}</title>
        {!!metaDescription && <meta name="description" content={metaDescription} />}
        <meta property="og:title" content={metaTitle} />
        {!!metaDescription && <meta property="og:description" content={metaDescription} />}
        {!!metaImage && <meta property="og:image" content={metaImage} />}
        <meta property="og:type" content="website" />
      </Head>
      <FloatingLanguagePicker />
      <ScrollView
        style={[styles.container, { backgroundColor: colors.background }]}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        onTouchEnd={handleDoubleTap}
      >
        {/* ── Header de boutique (inline, thémé) ── */}
        <View style={[styles.shopHeader, { backgroundColor: colors.card, borderColor: colors.border }]}>
          {shop.cover_image_url ? (
            <Image source={{ uri: shop.cover_image_url }} style={styles.shopCover} resizeMode="cover" />
          ) : (
            <View style={[styles.shopCoverPlaceholder, { backgroundColor: colors.background }]}>
              <Ionicons name="storefront-outline" size={40} color={colors.secondary} />
            </View>
          )}

          <View style={styles.shopHeaderBody}>
            <View style={styles.shopNameRow}>
              <Text style={[styles.shopName, { color: colors.text }]}>{shop.name}</Text>
              {shop.is_verified && (
                <Ionicons name="checkmark-circle" size={18} color={colors.primary} />
              )}
            </View>

            {header.show_tagline && !!shop.tagline && (
              <Text style={[styles.shopTagline, { color: colors.secondary }]}>{shop.tagline}</Text>
            )}

            {header.show_description && !!shop.description && (
              <Text style={[styles.shopDescription, { color: colors.text }]}>{shop.description}</Text>
            )}

            {header.show_address && !!(shop.city || shop.country || shop.address) && (
              <View style={styles.shopInfoRow}>
                <Ionicons name="location-outline" size={14} color={colors.secondary} />
                <Text style={[styles.shopInfoText, { color: colors.secondary }]}>
                  {[shop.address, shop.city, shop.country].filter(Boolean).join(', ')}
                </Text>
              </View>
            )}

            {header.show_phone && !!shop.contact_phone && (
              <TouchableOpacity style={styles.shopInfoRow} onPress={handleCall} activeOpacity={0.7}>
                <Ionicons name="call-outline" size={14} color={colors.secondary} />
                <Text style={[styles.shopInfoText, { color: colors.link }]}>{shop.contact_phone}</Text>
              </TouchableOpacity>
            )}

            {header.show_email && !!shop.contact_email && (
              <TouchableOpacity style={styles.shopInfoRow} onPress={handleEmail} activeOpacity={0.7}>
                <Ionicons name="mail-outline" size={14} color={colors.secondary} />
                <Text style={[styles.shopInfoText, { color: colors.link }]}>{shop.contact_email}</Text>
              </TouchableOpacity>
            )}

            <View style={styles.shopActionsRow}>
              <TouchableOpacity
                style={[styles.subscribeBtn, { backgroundColor: colors.button }]}
                activeOpacity={0.8}
                onPress={handleSubscribePress}
              >
                <Ionicons name="person-add-outline" size={16} color="#fff" />
                <Text style={styles.subscribeBtnText}>{t('publicShopScreen.subscribe')}</Text>
              </TouchableOpacity>

              {header.show_whatsapp_business && shop.show_whatsapp_business_button && !!shop.whatsapp_business_phone && (
                <TouchableOpacity style={styles.whatsappBtn} activeOpacity={0.8} onPress={handleWhatsApp}>
                  <Ionicons name="logo-whatsapp" size={16} color="#25D366" />
                  <Text style={styles.whatsappBtnText}>WhatsApp</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>

        {/* ── Produits ── */}
        <View style={styles.productsSection}>
          <Text style={[styles.productsSectionTitle, { color: colors.text }]}>{t('publicShopScreen.products')}</Text>

          {products.length === 0 && (
            <View style={styles.centerBox}>
              <Text style={styles.centerBoxText}>{t('publicShopScreen.noProducts')}</Text>
            </View>
          )}

          <View style={styles.productsGrid}>
            {products.map((product) => (
              <ThemedProductCard
                key={product.id}
                product={product}
                colors={colors}
                onPress={() => handleProductPress(product)}
              />
            ))}
          </View>
        </View>

        <View style={{ height: 40 }} />
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
    gap: 12,
    backgroundColor: '#fff',
  },
  centerText: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
  },
  backBtn: {
    marginTop: 8,
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: '#F3E8FF',
    borderRadius: 10,
  },
  backBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#6610B1',
  },
  shopHeader: {
    borderBottomWidth: 1,
  },
  shopCover: {
    width: '100%',
    height: 140,
  },
  shopCoverPlaceholder: {
    width: '100%',
    height: 140,
    justifyContent: 'center',
    alignItems: 'center',
  },
  shopHeaderBody: {
    padding: 16,
  },
  shopNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  shopName: {
    fontSize: 19,
    fontWeight: '800',
  },
  shopTagline: {
    fontSize: 13.5,
    marginTop: 4,
  },
  shopDescription: {
    fontSize: 13.5,
    marginTop: 8,
    lineHeight: 19,
  },
  shopInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
  },
  shopInfoText: {
    fontSize: 13,
  },
  shopActionsRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 14,
  },
  subscribeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 9,
    borderRadius: 10,
  },
  subscribeBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#fff',
  },
  whatsappBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 9,
    borderRadius: 10,
    backgroundColor: '#E9FBF0',
  },
  whatsappBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#25D366',
  },
  productsSection: {
    padding: 16,
  },
  productsSectionTitle: {
    fontSize: 16,
    fontWeight: '800',
    marginBottom: 12,
  },
  centerBox: {
    alignItems: 'center',
    paddingVertical: 24,
  },
  centerBoxText: {
    fontSize: 13,
    color: '#999',
  },
  productsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  productCard: {
    width: '48%',
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 2,
  },
  productImageWrap: {
    width: '100%',
    height: 100,
    borderRadius: 10,
    position: 'relative',
    overflow: 'hidden',
  },
  productImage: {
    width: '100%',
    height: '100%',
    backgroundColor: '#F3E8FF',
  },
  productImagePlaceholder: {
    width: '100%',
    height: 100,
    borderRadius: 10,
    backgroundColor: '#F3E8FF',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  featuredBadge: {
    position: 'absolute',
    top: 6,
    left: 6,
    borderRadius: 6,
    paddingHorizontal: 5,
    paddingVertical: 2,
  },
  featuredBadgeText: {
    fontSize: 8,
    fontWeight: '700',
    color: '#fff',
  },
  productName: {
    fontSize: 12.5,
    fontWeight: '700',
    color: '#222',
    marginTop: 8,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 5,
    marginTop: 3,
  },
  price: {
    fontSize: 13,
    fontWeight: '700',
  },
  promoPrice: {
    fontSize: 13,
    fontWeight: '700',
  },
  originalPrice: {
    fontSize: 11,
    color: '#999',
    textDecorationLine: 'line-through',
  },
});

// ── Styles du sélecteur de langue flottant ──
const langStyles = StyleSheet.create({
  trigger: {
    position: 'absolute',
    top: Platform.OS === 'ios' ? 54 : 30,
    right: 16,
    zIndex: 200,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#E8DEFF',
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 6,
    elevation: 4,
  },
  flag: { fontSize: 14 },
  label: { color: '#4B0082', fontSize: 12, fontWeight: '700' },
  backdrop: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 199,
  },
  menu: {
    position: 'absolute',
    top: Platform.OS === 'ios' ? 96 : 72,
    right: 16,
    backgroundColor: '#fff',
    borderRadius: 14,
    paddingVertical: 6,
    minWidth: 130,
    borderWidth: 1,
    borderColor: '#E8DEFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.15,
    shadowRadius: 16,
    elevation: 10,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    paddingHorizontal: 14,
    paddingVertical: 11,
  },
  menuItemActive: { backgroundColor: '#F3E8FF' },
  menuFlag: { fontSize: 15 },
  menuLabel: { flex: 1, fontSize: 13, fontWeight: '600', color: '#333' },
});
